import { api } from 'API'

// Athlete performance metrics (HF-1408). Embedded on the athlete profile so they
// stay scoped to the PROFILE id (not the account) and persist via the profile PATCH.
export enum AthleteMetricType {
  EXIT_VELOCITY = 'exit_velocity',
  PITCH_VELOCITY = 'pitch_velocity',
  SPIN_RATE = 'spin_rate'
}

export interface AthleteMetric {
  type: AthleteMetricType
  value: number
  /** ISO timestamp of when this value was last recorded; shown as the row date. */
  recordedAt?: string
}

export interface IAthleteProfile {
  id: number
  userId?: number
  ownerUserId?: number
  isManagedByGuardian?: boolean
  nickname?: string
  firstName?: string
  lastName?: string
  fullName?: string
  dob?: string
  gender?: string
  avatarUrl?: string
  graduationYear?: number
  schoolName?: string
  claimedAt?: string
  status?: string
  height?: number
  weight?: number
  heightUpdatedAt?: string
  weightUpdatedAt?: string
  level?: string
  primarySport?: string
  secondarySports?: string[]
  position?: string
  secondaryPositions?: string[]
  bats?: string
  throws?: string
  sports?: string[]
  instagram?: string
  twitterX?: string
  youtube?: string
  facebook?: string
  profileVisibility?: string
  metricsVisibility?: string
  metrics?: AthleteMetric[]
  // Profile media gallery — image URLs uploaded user-scoped (users/{userId}/{profileId}/media/).
  media?: string[]
}

export interface ICoachProfile {
  id: number
  userId?: number
  displayName?: string
  avatarUrl?: string
  coachingPhilosophy?: string
  yearsExperience?: number
  certifications?: string[]
  specialties?: string[]
  bio?: string
  publicVisibility?: string
}

export interface IMyProfilesResponse {
  athleteProfiles: IAthleteProfile[]
  coachProfile: ICoachProfile | null
}

export async function getMyProfiles(): Promise<IMyProfilesResponse> {
  const { data } = await api.get<IMyProfilesResponse>('/users/me/profiles')
  return data
}

export async function getAthleteProfile(profileId: number): Promise<IAthleteProfile> {
  const { data } = await api.get<IAthleteProfile>(`/athlete-profiles/${profileId}`)
  return data
}

export async function updateAthleteProfile(
  profileId: number,
  patch: Partial<IAthleteProfile>
): Promise<IAthleteProfile> {
  const { data } = await api.patch<IAthleteProfile>(`/athlete-profiles/${profileId}`, patch)
  return data
}

export async function createAthleteProfile(
  patch: Partial<IAthleteProfile>
): Promise<IAthleteProfile> {
  const { data } = await api.post<IAthleteProfile>('/athlete-profiles', patch)
  return data
}

export async function createChildAthleteProfile(
  patch: Partial<IAthleteProfile>
): Promise<IAthleteProfile> {
  const { data } = await api.post<IAthleteProfile>('/athlete-profiles/children', patch)
  return data
}

export async function deleteAthleteProfile(profileId: number): Promise<void> {
  await api.delete(`/athlete-profiles/${profileId}`)
}

export async function getCoachProfileMe(): Promise<ICoachProfile | null> {
  const { data } = await api.get<ICoachProfile | null>('/coach-profiles/me')
  return data
}

export async function createCoachProfile(displayName?: string): Promise<ICoachProfile> {
  const { data } = await api.post<ICoachProfile>('/coach-profiles', { displayName })
  return data
}

export async function updateCoachProfile(
  profileId: number,
  patch: Partial<ICoachProfile>
): Promise<ICoachProfile> {
  const { data } = await api.patch<ICoachProfile>(`/coach-profiles/${profileId}`, patch)
  return data
}

export interface IInitiateClaimResponse {
  token: string
  otpCode: string
  expiresAt: string
}

export async function initiateClaim(
  athleteProfileId: number,
  contactEmail: string
): Promise<IInitiateClaimResponse> {
  const { data } = await api.post<IInitiateClaimResponse>(
    `/athlete-profiles/${athleteProfileId}/initiate-claim`,
    { contactEmail }
  )
  return data
}

export async function verifyClaimOtp(token: string, otpCode: string): Promise<{ verified: boolean }> {
  const { data } = await api.post<{ verified: boolean }>(`/claim/${token}/verify-otp`, { otpCode })
  return data
}

export async function acceptClaim(token: string): Promise<IAthleteProfile> {
  const { data } = await api.post<IAthleteProfile>(`/claim/${token}/accept`, {})
  return data
}

export interface ITransferOwnershipResponse {
  oldOwnerMembership: { id: number; roleId: number }
  newOwnerMembership: { id: number; roleId: number }
}

export async function transferOwnership(
  orgId: number,
  newOwnerMembershipId: number
): Promise<ITransferOwnershipResponse> {
  const { data } = await api.post<ITransferOwnershipResponse>(
    `/organizations/${orgId}/transfer-ownership`,
    { newOwnerMembershipId }
  )
  return data
}
